import type { FertilityIntent } from "@src/packages/patients/patients.types";
import type { AcceptedTreatmentPlan } from "@src/packages/homepage/plan/treatmentPlanTypes";

export type FindCareFilterId =
  | "All"
  | "Saved"
  | "IVF"
  | "FET"
  | "Embryo Freezing"
  | "Egg Freezing"
  | "Sperm Freezing"
  | "IUI"
  | "PGT"
  | "Donor";

/** Stable route values for Find Care treatment searches. */
export type FindCareTreatmentIntent =
  | "ivf"
  | "fet"
  | "embryo-freezing"
  | "egg-freezing"
  | "sperm-freezing"
  | "iui"
  | "pgt"
  | "donor";

export type FindCarePatientContext = {
  gender?: string | null;
  treatmentGoal?: string | null;
  primaryIntent?: string | null;
  legacyTreatmentIntent?: string | null;
  plannedModalities?: FertilityIntent["plannedModalities"];
  acceptedTreatmentFamily?: AcceptedTreatmentPlan["treatmentFamily"] | null;
};

export function normalizePlannedModalities(
  values: readonly string[] | null | undefined,
): NonNullable<FindCarePatientContext["plannedModalities"]> {
  const modalities: NonNullable<FindCarePatientContext["plannedModalities"]> =
    [];

  for (const value of values ?? []) {
    if (
      value === "ivf" ||
      value === "iui" ||
      value === "oi" ||
      value === "egg_freezing" ||
      value === "embryo_freezing" ||
      value === "sperm_freezing" ||
      value === "fet" ||
      value === "not-sure"
    ) {
      modalities.push(value);
    }
  }

  return modalities;
}

const TREATMENT_INTENT_TO_FILTER: Record<
  FindCareTreatmentIntent,
  FindCareFilterId
> = {
  ivf: "IVF",
  fet: "FET",
  "embryo-freezing": "Embryo Freezing",
  "egg-freezing": "Egg Freezing",
  "sperm-freezing": "Sperm Freezing",
  iui: "IUI",
  pgt: "PGT",
  donor: "Donor",
};

export const FIND_CARE_TREATMENT_NAMES: Record<
  FindCareTreatmentIntent,
  string
> = {
  ivf: "IVF & ICSI",
  fet: "Frozen embryo transfer",
  "embryo-freezing": "Embryo freezing",
  "egg-freezing": "Egg Freezing",
  "sperm-freezing": "Sperm Freezing",
  iui: "IUI",
  pgt: "PGT",
  donor: "Donor programs",
};

/** Resolve only supported canonical route values. Unknown values stay neutral. */
export function resolveFindCareFilter(
  value: string | null | undefined,
): FindCareFilterId | null {
  switch (value) {
    case "ivf":
      return "IVF";
    case "fet":
      return "FET";
    case "embryo-freezing":
    case "embryo-preserve":
      return "Embryo Freezing";
    case "egg-freezing":
      return "Egg Freezing";
    case "sperm-freezing":
      return "Sperm Freezing";
    case "iui":
      return "IUI";
    case "pgt":
      return "PGT";
    case "donor":
      return "Donor";
    default:
      return null;
  }
}

function resolvePatientTreatmentIntent(
  context: FindCarePatientContext,
): FindCareTreatmentIntent | null {
  if (context.acceptedTreatmentFamily) {
    switch (context.acceptedTreatmentFamily) {
      case "ivf":
        return "ivf";
      case "iui":
        return "iui";
      case "egg_freezing":
        return "egg-freezing";
      case "embryo_freezing":
        return "embryo-freezing";
      case "sperm_freezing":
        return "sperm-freezing";
      case "fet":
        return "fet";
      case "undecided":
        // Do not silently fall back to an exploratory or inferred treatment.
        return null;
    }
  }

  const plannedModalities = context.plannedModalities ?? [];
  if (plannedModalities.length === 1) {
    const plannedModality = plannedModalities[0];
    if (plannedModality === "ivf" || plannedModality === "iui") {
      return plannedModality;
    }
    if (plannedModality === "fet") return "fet";
    if (plannedModality === "egg_freezing") return "egg-freezing";
    if (plannedModality === "embryo_freezing") return "embryo-freezing";
    if (plannedModality === "sperm_freezing") return "sperm-freezing";
  }

  switch (context.treatmentGoal ?? context.primaryIntent) {
    case "ivf":
      return "ivf";
    case "embryo-freezing":
    case "embryo-preserve":
      return "embryo-freezing";
    case "iui":
      return "iui";
    case "egg-freezing":
    case "egg-preserve":
      return "egg-freezing";
    case "sperm-freezing":
    case "sperm-preserve":
      return "sperm-freezing";
    case "egg-donation":
    case "sperm-donation":
      return "donor";
    case "preserve":
      if (context.gender === "AFAB") return "egg-freezing";
      if (context.gender === "AMAB") return "sperm-freezing";
      return null;
    case "donate":
      return "donor";
    default:
      break;
  }

  if (context.legacyTreatmentIntent === "Preservation") {
    if (context.gender === "AFAB") return "egg-freezing";
    if (context.gender === "AMAB") return "sperm-freezing";
  }

  if (context.legacyTreatmentIntent === "Donation") return "donor";

  return null;
}

/**
 * Return null while the patient context is unresolved so the screen cannot
 * present All as a premature default. Once resolved, unknown context is All.
 */
export function resolvePatientDefaultFilter(
  context: FindCarePatientContext & { isLoading: boolean },
): FindCareFilterId | null {
  if (context.isLoading) return null;

  const treatmentIntent = resolvePatientTreatmentIntent(context);
  return treatmentIntent ? TREATMENT_INTENT_TO_FILTER[treatmentIntent] : "All";
}

export function getInitialFindCareFilter({
  routeIntent,
  patient,
  isPatientLoading,
}: {
  routeIntent?: string | null;
  patient: FindCarePatientContext;
  isPatientLoading: boolean;
}): FindCareFilterId | null {
  if (routeIntent !== undefined && routeIntent !== null) {
    return resolveFindCareFilter(routeIntent) ?? "All";
  }

  return resolvePatientDefaultFilter({
    ...patient,
    isLoading: isPatientLoading,
  });
}

/** Apply a delayed patient default only while the user has not made a choice. */
export function applyPatientDefaultIfUnselected({
  currentFilter,
  hasManualSelection,
  patientDefault,
}: {
  currentFilter: FindCareFilterId | null;
  hasManualSelection: boolean;
  patientDefault: FindCareFilterId | null;
}): FindCareFilterId | null {
  if (currentFilter !== null || hasManualSelection) return currentFilter;
  return patientDefault;
}

export function getTreatmentIntentForFilter(
  filter: FindCareFilterId | null,
): FindCareTreatmentIntent | null {
  switch (filter) {
    case "IVF":
      return "ivf";
    case "FET":
      return "fet";
    case "Embryo Freezing":
      return "embryo-freezing";
    case "Egg Freezing":
      return "egg-freezing";
    case "Sperm Freezing":
      return "sperm-freezing";
    case "IUI":
      return "iui";
    case "PGT":
      return "pgt";
    case "Donor":
      return "donor";
    default:
      return null;
  }
}
